import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useOrgId } from './org';

export type ContactKind = 'customer' | 'vendor' | 'both';

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  emirate: string | null;
  poBox: string | null;
  /** PIN or postal code (India). */
  postalCode?: string | null;
  country: string;
}

export interface Contact {
  id: string;
  kind: ContactKind;
  displayName: string;
  companyName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  website: string | null;
  vatRegistered: boolean;
  trn: string | null;
  country: string;
  emirate: string | null;
  /** India: GSTIN and GST state (default place of supply). */
  gstin?: string | null;
  stateCode?: string | null;
  billingAddress: Address | null;
  shippingAddress: Address | null;
  defaultTaxCodeId: string | null;
  defaultTaxCode: string | null;
  paymentTermsDays: number;
  defaultAccountId: string | null;
  defaultAccountName: string | null;
  bankName: string | null;
  iban: string | null;
  notes: string | null;
  isActive: boolean;
  hasTransactions: boolean;
  receivable: string;
  payable: string;
  /** The contact's currency (home unless foreign); fixed once it has transactions. */
  currency: string;
  receivableForeign: string | null;
  payableForeign: string | null;
  createdAt: string;
}

export interface ContactTransaction {
  entryId: string;
  entryNo: string;
  date: string;
  sourceType: string;
  memo: string | null;
  lineNo: number;
  accountCode: string;
  accountName: string;
  description: string | null;
  taxCode: string | null;
  debit: string | null;
  credit: string | null;
  currency: string;
  foreignDebit: string | null;
  foreignCredit: string | null;
  status: 'posted' | 'reversed';
}

export type ContactStatus = 'active' | 'inactive' | 'all';

/** Contacts of the open client; without a kind, every active contact (name pickers). */
export function useContacts(opts: { kind?: 'customer' | 'vendor'; status?: ContactStatus } = {}) {
  const orgId = useOrgId();
  const status = opts.status ?? 'active';
  return useQuery({
    queryKey: ['contacts', orgId, opts.kind ?? 'all', status],
    queryFn: () =>
      api<Contact[]>(
        `/orgs/${orgId}/contacts?status=${status}${opts.kind ? `&kind=${opts.kind}` : ''}`,
      ),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

export function useContact(id: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['contacts', orgId, 'one', id],
    queryFn: () => api<Contact>(`/orgs/${orgId}/contacts/${id}`),
    enabled: Boolean(orgId),
  });
}

export function useContactTransactions(id: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['contacts', orgId, 'transactions', id],
    queryFn: () => api<ContactTransaction[]>(`/orgs/${orgId}/contacts/${id}/transactions`),
    enabled: Boolean(orgId),
  });
}

/** Countries offered in address pickers; any ISO code is accepted by the API and imports. */
export const COUNTRIES: Record<string, string> = {
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  OM: 'Oman',
  QA: 'Qatar',
  BH: 'Bahrain',
  KW: 'Kuwait',
  EG: 'Egypt',
  JO: 'Jordan',
  LB: 'Lebanon',
  TR: 'Türkiye',
  IN: 'India',
  PK: 'Pakistan',
  LK: 'Sri Lanka',
  BD: 'Bangladesh',
  PH: 'Philippines',
  CN: 'China',
  HK: 'Hong Kong',
  SG: 'Singapore',
  JP: 'Japan',
  GB: 'United Kingdom',
  IE: 'Ireland',
  DE: 'Germany',
  FR: 'France',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  CH: 'Switzerland',
  US: 'United States',
  CA: 'Canada',
  AU: 'Australia',
  ZA: 'South Africa',
};

export const countryName = (code: string): string => COUNTRIES[code] ?? code;

/** Multi-line address for read views. */
export function formatAddress(a: Address | null): string {
  if (!a) return '';
  const place = [...new Set([a.city, a.emirate].filter(Boolean))].join(', ');
  const lines = [
    a.line1,
    a.line2,
    a.postalCode ? `${place} ${a.postalCode}`.trim() : place,
    a.poBox ? `P.O. Box ${a.poBox}` : null,
  ].filter(Boolean);
  if (!lines.length) return '';
  return [...lines, countryName(a.country)].join('\n');
}

export const KIND_LABEL: Record<'customer' | 'vendor', { one: string; many: string }> = {
  customer: { one: 'Customer', many: 'Customers' },
  // "Vendors" vs "Suppliers" is open question 6 (document 13); one string, changed here.
  vendor: { one: 'Vendor', many: 'Vendors' },
};

export const LIST_PATH = {
  customer: '/sales/customers',
  vendor: '/expenses/vendors',
} as const;
